import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ERROR_MESSAGE_WEATHER_MODULE__500,
  ERROR_MESSAGE_WEATHER_OUTLOOK__204,
} from '../../../../configurations/error-messages';
import { callEndpoint, telemetry } from '../../../../services/backend';
import {
  selectRiskForecastVesselLength,
  selectRiskForecastWatercraftType,
} from '../../../../services/store/modules/app-controls';
import { selectSelectedVessel } from '../../../../services/store/modules/vessels';
import { getVesselLengthInMeters } from '../../Vessels/utils/vessel-length';
import {
  formatWeatherForecastDays,
  type WeatherForecastDay,
  type WeatherOutlookApiResponse,
} from '../helpers/outlook/formatDays';
import { safelyFillHourlyGapsForWeatherGraphType } from '../helpers/graph/graphData';
import { type WeatherGraphPoint } from '../helpers/risk/riskLevels';
import { resolveVesselProfile } from '../helpers/risk/vesselProfileSelection';
import { resolveWaterBody } from '../helpers/risk/waterBody';
import { FREE_FORECAST_DAYS } from '../helpers/forecastHorizon';

export interface WeatherForecastOutlookError {
  status?: number;
  type?: 'warning';
  message: string;
}

interface WeatherForecastGraphResponse {
  points?: WeatherGraphPoint[];
}

const hasValidCoordinates = (
  latitude: number | undefined,
  longitude: number | undefined,
) =>
  latitude != null &&
  longitude != null &&
  Number.isFinite(latitude) &&
  Number.isFinite(longitude);

export const useWeatherForecastOutlook = (
  latitude: number | undefined,
  longitude: number | undefined,
  days = FREE_FORECAST_DAYS,
) => {
  const savedVesselLength = useSelector(selectRiskForecastVesselLength);
  const savedWatercraftType = useSelector(selectRiskForecastWatercraftType);
  const selectedVessel = useSelector(selectSelectedVessel);
  const [forecastDays, setForecastDays] = useState<WeatherForecastDay[]>([]);
  const [timeZone, setTimeZone] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<WeatherForecastOutlookError | null>(null);

  const vesselLength = (() => {
    const selectedVesselLength = selectedVessel
      ? getVesselLengthInMeters(selectedVessel)
      : undefined;

    return selectedVesselLength ?? savedVesselLength;
  })();

  const vesselType = selectedVessel?.watercraft_type ?? savedWatercraftType;
  const vessel = useMemo(
    () => resolveVesselProfile(vesselType, vesselLength),
    [vesselType, vesselLength],
  );

  useEffect(() => {
    let isMounted = true;

    if (!hasValidCoordinates(latitude, longitude)) {
      setForecastDays([]);
      setTimeZone(undefined);
      setError(null);
      setIsLoading(true);
      return () => {
        isMounted = false;
      };
    }

    const loadOutlook = async () => {
      setIsLoading(true);
      setError(null);
      setForecastDays([]);
      setTimeZone(undefined);

      try {
        const queryParams = {
          latitude: latitude as number,
          longitude: longitude as number,
          days,
        };

        // Outlook is required; wind/swell are optional (unknown risk bars).
        // Match graph cards: isolate partial graph failures via allSettled.
        const [outlookResult, windResult, swellResult, swellPeriodResult] =
          await Promise.allSettled([
            callEndpoint(
              callEndpoint.endpoints.queryWeatherOutlook,
              {},
              queryParams,
            ) as Promise<WeatherOutlookApiResponse>,
            callEndpoint(
              callEndpoint.endpoints.queryWeatherForecastGraphData,
              {},
              { ...queryParams, type: 'wind' },
            ) as Promise<WeatherForecastGraphResponse>,
            callEndpoint(
              callEndpoint.endpoints.queryWeatherForecastGraphData,
              {},
              { ...queryParams, type: 'swell-height' },
            ) as Promise<WeatherForecastGraphResponse>,
            callEndpoint(
              callEndpoint.endpoints.queryWeatherForecastGraphData,
              {},
              { ...queryParams, type: 'swell-period' },
            ) as Promise<WeatherForecastGraphResponse>,
          ]);

        if (!isMounted) {
          return;
        }

        if (outlookResult.status === 'rejected') {
          throw outlookResult.reason;
        }

        if (windResult.status === 'rejected') {
          telemetry(
            telemetry.ERROR,
            `[useWeatherForecastOutlook.loadOutlook] wind failed: ${JSON.stringify(windResult.reason)}`,
          );
        }

        if (swellResult.status === 'rejected') {
          telemetry(
            telemetry.ERROR,
            `[useWeatherForecastOutlook.loadOutlook] swell-height failed: ${JSON.stringify(swellResult.reason)}`,
          );
        }

        if (swellPeriodResult.status === 'rejected') {
          telemetry(
            telemetry.ERROR,
            `[useWeatherForecastOutlook.loadOutlook] swell-period failed: ${JSON.stringify(swellPeriodResult.reason)}`,
          );
        }

        const weatherOutlookData = outlookResult.value;
        const windDataResponse =
          windResult.status === 'fulfilled' ? windResult.value : undefined;
        const swellDataResponse =
          swellResult.status === 'fulfilled' ? swellResult.value : undefined;
        const swellPeriodDataResponse =
          swellPeriodResult.status === 'fulfilled'
            ? swellPeriodResult.value
            : undefined;

        if (
          weatherOutlookData.constructor === Object &&
          Object.entries(weatherOutlookData).length === 0
        ) {
          setError({
            status: 204,
            type: 'warning',
            message: ERROR_MESSAGE_WEATHER_OUTLOOK__204,
          });
          return;
        }

        const outlookTimeZone = weatherOutlookData?.weatherLocation?.timeZone;

        const swellHeightPoints = safelyFillHourlyGapsForWeatherGraphType(
          swellDataResponse?.points,
          'swell-height',
          outlookTimeZone,
        );

        const formattedForecastDays = formatWeatherForecastDays(
          weatherOutlookData,
          vessel,
          resolveWaterBody(swellHeightPoints),
          {
            wind: safelyFillHourlyGapsForWeatherGraphType(
              windDataResponse?.points,
              'wind',
              outlookTimeZone,
            ),
            swellHeight: swellHeightPoints,
            swellPeriod: safelyFillHourlyGapsForWeatherGraphType(
              swellPeriodDataResponse?.points,
              'swell-period',
              outlookTimeZone,
            ),
          },
          days,
        );

        if (!formattedForecastDays.length) {
          setError({
            status: 204,
            type: 'warning',
            message: ERROR_MESSAGE_WEATHER_OUTLOOK__204,
          });
          return;
        }

        setForecastDays(formattedForecastDays);
        setTimeZone(weatherOutlookData?.weatherLocation?.timeZone);
      } catch (loadError) {
        telemetry(
          telemetry.ERROR,
          `[useWeatherForecastOutlook.loadOutlook] ${JSON.stringify(loadError)}`,
        );

        if (isMounted) {
          setError({
            message: ERROR_MESSAGE_WEATHER_MODULE__500,
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadOutlook();

    return () => {
      isMounted = false;
    };
  }, [days, latitude, longitude, vessel]);

  return {
    forecastDays,
    timeZone,
    isLoading,
    error,
  };
};
